import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import {
  Component,
  OnInit,
  inject,
  output,
  ChangeDetectionStrategy,
} from "@angular/core";
import { OnDestroy } from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import { Subject, Subscription } from "rxjs";
import { addMonths, differenceInDays, endOfMonth } from "date-fns";
import { DateRange } from "../../shared/date/date-range.model";
import { placeholder } from "../../ui/wysiwyg/config/placeholder.const";
import { StripHtmlTagsPipe } from "../../shared/html/strip-html-tags.pipe";
import { validateEmailArrayFormControl } from "app/shared/email/email-array-validator";
import { QuoteEmail } from "../models/email.model";
import { DatePickerComponent } from "../../ui/date-picker/date-picker.component";
import { Bind } from "primeng/bind";
import { ButtonDirective } from "primeng/button";
import { AutoComplete } from "primeng/autocomplete";
import { WysiwygComponent } from "../../ui/wysiwyg/wysiwyg.component";

@Component({
  selector: "app-quote-batch-mail-editor",
  templateUrl: "./quote-batch-mail-editor.component.html",
  styleUrls: ["./quote-batch-mail-editor.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ButtonDirective,
    FormsModule,
    ReactiveFormsModule,
    DatePickerComponent,
    Bind,
    AutoComplete,
    WysiwygComponent,
  ],
})
export class QuoteBatchMailEditorComponent implements OnInit, OnDestroy {
  private formBuilder = inject(UntypedFormBuilder);

  readonly batchSend = output<{
    validityDates: DateRange;
    quoteEmail: QuoteEmail;
  }>();

  public activeEditorIndex = 1;
  public mailForm: UntypedFormGroup;
  public wysiwygChange = new Subject();
  public validityDates: DateRange = new DateRange(
    new Date(),
    endOfMonth(new Date()),
  );
  private subscription = new Subscription();
  private separatorKeys = [" ", ";", ",", "Tab"];
  private replaceKeys = [" ", ";", ","];
  public results = [];

  ngOnInit() {
    this.createForm();
    this.fillform();
    this.observeFormChanges();
    this.setBatchDatePickerOptions();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.wysiwygChange.unsubscribe();
  }

  private setBatchDatePickerOptions() {
    if (differenceInDays(endOfMonth(new Date()), new Date()) <= 1) {
      this.setValidityDates(
        new DateRange(new Date(), endOfMonth(addMonths(new Date(), 1))),
      );
    }
  }

  public isOpened(): boolean {
    return this.activeEditorIndex === 0;
  }

  public open() {
    this.activeEditorIndex = 0;
  }

  public close() {
    this.activeEditorIndex = 1;
  }

  public wrapPlaceholder(placeholder: string, startTag = "[[", endTag = "]]") {
    return startTag + placeholder + endTag;
  }

  public getFormDefaults(): object {
    return {
      to: [this.wrapPlaceholder(placeholder.clientEmails)],
      cc: [this.wrapPlaceholder(placeholder.otherSalesReps)],
      bcc: [],
      subject: `${this.wrapPlaceholder(placeholder.myCompanyName)} offer ${this.wrapPlaceholder(placeholder.quoteNumber)} for ${this.wrapPlaceholder(placeholder.clientName)} ${this.wrapPlaceholder(placeholder.validityDates)}`,
      message: `${this.wrapPlaceholder(placeholder.clientGreetings)}<br/><br/>${this.wrapPlaceholder(placeholder.quoteSummary)}<br/><br/>${this.wrapPlaceholder(placeholder.mySignature)}`,
    };
  }

  public setValidityDates(dateRange: DateRange): void {
    this.validityDates = dateRange;
    this.mailForm
      .get("validFrom")
      .setValue(this.validityDates.getStartDateString());
    this.mailForm
      .get("validTo")
      .setValue(this.validityDates.getEndDateString());
    this.mailForm.updateValueAndValidity();
  }

  public setField(fieldKey: string, value: string, removeTags: boolean) {
    if (removeTags) {
      value = new StripHtmlTagsPipe().transform(value);
    }
    this.mailForm.get(fieldKey).setValue(value);
    this.mailForm.updateValueAndValidity();
  }

  public search(event) {
    this.results = [
      event.query,
      "[[" + placeholder.clientEmails + "]]",
      "[[" + placeholder.myEmail + "]]",
      "[[" + placeholder.otherSalesReps + "]]",
    ];
  }

  public onKeyUpField(event, targetField: string = "to"): void {
    if (this.separatorKeys.indexOf(event.key) !== -1) {
      let value = event.currentTarget.value;
      for (let i = 0; i < this.replaceKeys.length; i++) {
        value = value.replace(this.replaceKeys[i], "");
      }
      if (this.isValidEmail(value)) {
        event.currentTarget.value = "";

        const mails = Array.isArray(this.mailForm.controls[targetField].value)
          ? this.mailForm.controls[targetField].value
          : [];
        mails.push(value);
        this.mailForm.patchValue({
          targetField: mails,
        });
        this.mailForm.controls[targetField].updateValueAndValidity();
      }
    }
  }

  public onBlurField(event, targetField: string = "to"): void {
    const value = event.currentTarget.value;
    if (this.isValidEmail(value)) {
      event.currentTarget.value = "";
      const mails = Array.isArray(this.mailForm.controls[targetField].value)
        ? this.mailForm.controls[targetField].value
        : [];
      mails.push(value);
      this.mailForm.patchValue({
        targetField: mails,
      });
      this.mailForm.controls[targetField].updateValueAndValidity();
    }
  }

  public send(): void {
    this.batchSend.emit({
      validityDates: this.validityDates,
      quoteEmail: new QuoteEmail(
        this.mailForm.controls.to.value.join(","),
        this.mailForm.controls.cc.value.join(","),
        this.mailForm.controls.bcc.value.join(","),
        this.mailForm.controls.subject.value
          .replace(/<\/?[^>]+(>|$)/g, "")
          .replaceAll("&nbsp;", " "),
        this.mailForm.controls.message.value,
      ),
    });
  }

  private isValidEmail(email): boolean {
    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    return re.test(String(email).toLowerCase());
  }

  private createForm() {
    this.mailForm = this.formBuilder.group({
      validFrom: [this.validityDates.getStartDateString(), Validators.required],
      validTo: [this.validityDates.getEndDateString(), Validators.required],
      to: [[], [validateEmailArrayFormControl, Validators.required]],
      cc: [[], [validateEmailArrayFormControl]],
      bcc: [[], [validateEmailArrayFormControl]],
      subject: ["", Validators.required],
      message: ["", Validators.required],
    });
  }

  private fillform() {
    this.mailForm.patchValue(this.getFormDefaults());
    this.mailForm.updateValueAndValidity();
  }

  private observeFormChanges() {
    this.subscription.add(
      this.mailForm.valueChanges
        .pipe(distinctUntilChanged(), debounceTime(1000))
        .subscribe((change) => {
          this.mailForm.updateValueAndValidity();
        }),
    );
  }
}
